import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ShieldCheck, FileText, ListChecks, CalendarClock, Mail, AlertTriangle,
  ArrowRight, Send, ChevronRight, Loader2, UserCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import FlagToggle from '../components/FlagToggle';
import HussarTopNav from '../components/HussarTopNav';
import IntegrityCapability from '../components/IntegrityCapability';
import ReviewSignalsSection from '../components/ReviewSignalsSection';
import ProtocolBadge from '../components/ProtocolBadge';
import useRouteMeta from '../hooks/useRouteMeta';
import { track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComplianceOperator = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const titlePl = 'Operator Zgodności: przygotowanie spraw pod kontrolą człowieka | HussarAi';
  const titleEn = 'Compliance Operator: case preparation under human control | HussarAi';
  const descPl =
    'HussarAi pomaga organizacjom porządkować dokumenty, wskazywać braki, tworzyć projekty odpowiedzi i zachować ślad audytowy — bez automatycznych decyzji.';
  const descEn =
    'HussarAi helps organizations organise documents, flag formal gaps, draft replies, and preserve an audit trail — without automated decisions.';
  const pageUrl = `${origin}/operator-zgodnosci`;
  const imageUrl = `${origin}/og/operator-zgodnosci-og.png`;

  useRouteMeta({
    marker: 'operator-zgodnosci',
    title: lang === 'en' ? titleEn : titlePl,
    description: lang === 'en' ? descEn : descPl,
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogImage: imageUrl,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogUrl: pageUrl,
  });

  useEffect(() => {
    track('compliance_page_view', {});
  }, []);

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://hussarai.org/operator-zgodnosci#software',
        'name': 'HussarAi Operator Zgodności',
        'applicationCategory': 'BusinessApplication',
        'applicationSubCategory': 'AI Compliance Process Operator',
        'operatingSystem': 'Web',
        'inLanguage': ['pl'],
        'description': t('compliance.metaDescription'),
        'url': 'https://hussarai.org/operator-zgodnosci',
        'offers': { '@type': 'Offer', 'price': '0', 'priceCurrency': 'PLN', 'category': 'demo' },
      },
      {
        '@type': 'Organization',
        '@id': 'https://hussarai.org/#org',
        'name': 'HussarAi',
        'url': 'https://hussarai.org',
      },
      {
        '@type': 'BreadcrumbList',
        'itemListElement': [
          { '@type': 'ListItem', 'position': 1, 'name': 'HussarAi',           'item': 'https://hussarai.org/' },
          { '@type': 'ListItem', 'position': 2, 'name': 'Operator Zgodności', 'item': 'https://hussarai.org/operator-zgodnosci' },
        ],
      },
    ],
  }), [t]);

  const scrollToDemo = () => {
    track('compliance_cta_clicked', { target: 'demo_form' });
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="hussar-landing" data-testid="compliance-page">
      {/* JSON-LD structured data — safe-by-design (local constant, no user input). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
      />

      <HussarTopNav active="compliance" />

      <header className="hussar-landing-header">
        <div className="hussar-landing-header-content">
          <Link to="/" className="hussar-logo-full">
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </Link>
          <div className="hussar-landing-header-actions">
            <FlagToggle />
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="compliance-nav-login">
              {t('agenci.nav.login')}
            </Button>
          </div>
        </div>
      </header>

      <main className="hussar-agenci-main">
        <section className="hussar-agenci-hero" data-testid="compliance-hero">
          <div className="hussar-agenci-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" />
            {t('compliance.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="compliance-h1">{t('compliance.h1')}</h1>
          <p className="hussar-agenci-subhead" data-testid="compliance-subhead">{t('compliance.subhead')}</p>
          <p className="hussar-agenci-proof hussar-proof-strong" data-testid="compliance-trustline">
            {t('compliance.trustLine')}
          </p>
          <ProtocolBadge testidPrefix="compliance-hero" />
          <div className="hussar-agenci-cta-row">
            <Button size="lg" onClick={scrollToDemo} className="hussar-btn-primary hussar-btn-large"
                    data-testid="compliance-cta-demo">
              {t('compliance.ctaDemo')} <ArrowRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={scrollToDemo} data-testid="compliance-cta-submit">
              {t('compliance.ctaSubmit')} <ChevronRight className="w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => { track('compliance_cta_clicked', { target: 'waitlist' }); navigate('/login'); }}
                    data-testid="compliance-cta-waitlist">
              {t('compliance.ctaWaitlist')} <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          {/* People-first connectivity link — connects the institutional
              surface back to the grassroots entry point. v3.1 single-promise
              bridge. */}
          <p
            data-testid="compliance-people-bridge"
            style={{
              marginTop: 16, fontSize: 13, lineHeight: 1.55,
              color: 'rgba(226,232,240,0.72)',
            }}
          >
            <Link
              to="/moje-akta-imigracyjne"
              data-testid="compliance-people-bridge-link"
              style={{
                color: '#7DD3FC', fontWeight: 600, textDecoration: 'none',
              }}
            >
              {t('brandPivot.heroCtaPeople')} →
            </Link>
            <span style={{ marginLeft: 10 }}>
              {t('compliance.peopleBridgeHelper')}
            </span>
          </p>
        </section>

        <section className="hussar-pol-op-section" data-testid="compliance-problem">
          <h2 className="hussar-agenci-h2">{t('compliance.problem.title')}</h2>
          <ul className="hussar-pol-op-bullets">
            {t('compliance.problem.bullets', { returnObjects: true }).map((b, i) => (
              <li key={b} data-testid={`compliance-problem-${i}`}>{b}</li>
            ))}
          </ul>
        </section>

        <section className="hussar-pol-op-section" data-testid="compliance-how">
          <h2 className="hussar-agenci-h2">{t('compliance.how.title')}</h2>
          <ol className="hussar-pol-op-bullets" style={{ listStyle: 'decimal', paddingLeft: '24px' }}>
            {t('compliance.how.steps', { returnObjects: true }).map((step, i) => (
              <li key={step} data-testid={`compliance-how-${i}`}>{step}</li>
            ))}
          </ol>
        </section>

        <section className="hussar-pol-op-section" data-testid="compliance-use-cases">
          <h2 className="hussar-agenci-h2">{t('compliance.useCases.title')}</h2>
          <div className="hussar-agenci-deliverables-grid">
            {t('compliance.useCases.items', { returnObjects: true }).map((item, i) => (
              <Card key={item} className="hussar-agenci-deliverable-card" data-testid={`compliance-uc-${i}`}>
                <div className="hussar-agenci-deliverable-icon"><UserCheck className="w-5 h-5" /></div>
                <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>{item}</h3>
              </Card>
            ))}
          </div>
        </section>

        {/* v4.0 Phase 5 — Review signals for immigration record integrity. */}
        <section className="hussar-pol-op-section" data-testid="compliance-review-signals-v4">
          <h2 className="hussar-agenci-h2" data-testid="compliance-review-signals-v4-title">
            <AlertTriangle className="w-6 h-6 inline-block mr-2" />
            {t('compliance.reviewSignalsV4.title')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="compliance-review-signals-v4-intro">
            {t('compliance.reviewSignalsV4.intro')}
          </p>
          <ul className="hussar-pol-op-bullets" data-testid="compliance-review-signals-v4-list">
            {(t('compliance.reviewSignalsV4.items', { returnObjects: true }) || []).map((item, i) => (
              <li key={item} data-testid={`compliance-review-signal-v4-${i}`}>{item}</li>
            ))}
          </ul>
          <p className="hussar-pol-op-body" data-testid="compliance-review-signals-v4-framing"
             style={{ marginTop: '18px', fontStyle: 'italic' }}>
            {t('compliance.reviewSignalsV4.framing')}
          </p>
          <p className="hussar-pol-op-body" data-testid="compliance-review-signals-v4-integrity"
             style={{ marginTop: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 600 }}>
            {t('compliance.reviewSignalsV4.integrity')}
          </p>
        </section>

        {/* Administration / strategic enterprise section — strictly process-
            organisation framing, no decisions, no legal claims. */}
        <section className="hussar-pol-op-section" data-testid="compliance-admin">
          <h2 className="hussar-agenci-h2">{t('compliance.administration.title')}</h2>
          <p className="hussar-pol-op-body">{t('compliance.administration.body')}</p>
          <div className="hussar-agenci-deliverables-grid">
            {t('compliance.administration.cards', { returnObjects: true }).map((card, i) => (
              <Card
                key={card.title}
                className="hussar-agenci-deliverable-card"
                data-testid={`compliance-admin-card-${i}`}
              >
                <div className="hussar-agenci-deliverable-icon">
                  {[<FileText key="0" className="w-5 h-5" />,
                    <ListChecks key="1" className="w-5 h-5" />,
                    <ShieldCheck key="2" className="w-5 h-5" />,
                    <Mail key="3" className="w-5 h-5" />][i] || <UserCheck className="w-5 h-5" />}
                </div>
                <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>{card.title}</h3>
                <p className="hussar-agenci-deliverable-body">{card.body}</p>
              </Card>
            ))}
          </div>
          <p className="hussar-pol-op-body" style={{ marginTop: '20px', fontStyle: 'italic' }}
             data-testid="compliance-admin-trust">
            {t('compliance.administration.trust')}
          </p>
        </section>

        <section className="hussar-pol-op-section" data-testid="compliance-disclaimer">
          <h2 className="hussar-agenci-h2">
            <AlertTriangle className="w-6 h-6 inline-block mr-2" />
            {t('compliance.disclaimer.title')}
          </h2>
          <p className="hussar-pol-op-body">{t('compliance.disclaimer.body')}</p>
        </section>

        <section className="hussar-pol-op-section" data-testid="compliance-human">
          <h2 className="hussar-agenci-h2">
            <UserCheck className="w-6 h-6 inline-block mr-2" />
            {t('compliance.humanApproval.title')}
          </h2>
          <p className="hussar-pol-op-body">{t('compliance.humanApproval.body')}</p>
        </section>

        <ComplianceDemoForm />
        <IntegrityCapability testIdPrefix="compliance-integrity" />

        <section className="hussar-pol-op-section hussar-pol-op-footer-cta" data-testid="compliance-footer-cta">
          <h2 className="hussar-agenci-h2">{t('compliance.footerCta.title')}</h2>
          <p className="hussar-pol-op-body">{t('compliance.footerCta.body')}</p>
          <div className="hussar-agenci-cta-row">
            <Button size="lg" onClick={scrollToDemo} className="hussar-btn-primary hussar-btn-large"
                    data-testid="compliance-footer-cta-btn">
              {t('compliance.footerCta.button')} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        <footer className="hussar-pol-op-footer" data-testid="compliance-footer">
          <p>{t('compliance.tagline')}</p>
          <p className="hussar-pol-op-footer-meta">© HussarAi · audyt@hussarai.org</p>
        </footer>
      </main>
    </div>
  );
};

/* -------------------- Interactive Compliance Demo Form -------------------- */
const initialForm = {
  companyType: '',
  processType: '',
  documents: '',
  knownDates: '',
  issue: '',
  tone: 'neutral',
  language: 'pl',
  email: '',
  consent: false,
};

const ComplianceDemoForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [output, setOutput] = useState(null);
  const [error, setError] = useState('');

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setOutput(null);
    if (!(form.companyType.trim() || form.processType.trim() || form.documents.trim() || form.issue.trim())) {
      setError(t('compliance.form.errorValidation'));
      return;
    }
    setSubmitting(true);
    track('compliance_demo_started', { tone: form.tone, language: form.language });
    try {
      const r = await axios.post(`${API}/compliance-operator/demo`, form);
      setOutput(r.data);
      track('compliance_demo_submitted', { submissionId: r.data.submissionId });
      track('compliance_output_generated', { hasEmail: !!form.email });
    } catch (e) {
      setError(e?.response?.data?.detail || t('compliance.form.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo-form" className="hussar-pol-op-section" data-testid="compliance-demo-form">
      <h2 className="hussar-agenci-h2">{t('compliance.form.title')}</h2>
      <p className="hussar-pol-op-body">{t('compliance.form.intro')}</p>

      <Card className="hussar-tc-card hussar-compliance-form" data-testid="compliance-form-card">
        <form onSubmit={submit} className="hussar-cb-field" style={{ marginBottom: 0 }}>
          <Field label={t('compliance.form.companyTypeLabel')}>
            <Input value={form.companyType} onChange={(e) => upd('companyType', e.target.value)}
                   placeholder={t('compliance.form.companyTypePlaceholder')}
                   data-testid="compliance-form-companyType" />
          </Field>
          <Field label={t('compliance.form.processTypeLabel')}>
            <Input value={form.processType} onChange={(e) => upd('processType', e.target.value)}
                   placeholder={t('compliance.form.processTypePlaceholder')}
                   data-testid="compliance-form-processType" />
          </Field>
          <Field label={t('compliance.form.documentsLabel')}>
            <Textarea rows={4} value={form.documents} onChange={(e) => upd('documents', e.target.value)}
                      placeholder={t('compliance.form.documentsPlaceholder')}
                      data-testid="compliance-form-documents" />
          </Field>
          <Field label={t('compliance.form.knownDatesLabel')}>
            <Textarea rows={2} value={form.knownDates} onChange={(e) => upd('knownDates', e.target.value)}
                      placeholder={t('compliance.form.knownDatesPlaceholder')}
                      data-testid="compliance-form-knownDates" />
          </Field>
          <Field label={t('compliance.form.issueLabel')}>
            <Textarea rows={3} value={form.issue} onChange={(e) => upd('issue', e.target.value)}
                      placeholder={t('compliance.form.issuePlaceholder')}
                      data-testid="compliance-form-issue" />
          </Field>
          <div className="hussar-compliance-form-row">
            <Field label={t('compliance.form.toneLabel')}>
              <Select value={form.tone} onValueChange={(v) => upd('tone', v)}>
                <SelectTrigger data-testid="compliance-form-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="formal">{t('compliance.form.toneOptions.formal')}</SelectItem>
                  <SelectItem value="neutral">{t('compliance.form.toneOptions.neutral')}</SelectItem>
                  <SelectItem value="friendly">{t('compliance.form.toneOptions.friendly')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t('compliance.form.languageLabel')}>
              <Select value={form.language} onValueChange={(v) => upd('language', v)}>
                <SelectTrigger data-testid="compliance-form-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pl">PL</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={t('compliance.form.emailLabel')}>
            <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)}
                   placeholder={t('compliance.form.emailPlaceholder')}
                   data-testid="compliance-form-email" />
          </Field>
          <div className="hussar-compliance-consent">
            <Checkbox id="compliance-consent" checked={form.consent}
                      onCheckedChange={(v) => upd('consent', !!v)}
                      data-testid="compliance-form-consent" />
            <label htmlFor="compliance-consent">{t('compliance.form.consentLabel')}</label>
          </div>
          {error && (
            <p className="hussar-compliance-error" data-testid="compliance-form-error">{error}</p>
          )}
          <div style={{ marginTop: '16px' }}>
            <Button type="submit" size="lg" className="hussar-btn-primary"
                    disabled={submitting} data-testid="compliance-form-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? t('compliance.form.submitting') : t('compliance.form.submit')}
            </Button>
          </div>
        </form>
      </Card>

      {output && <ComplianceDemoOutput output={output} />}
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="hussar-cb-field">
    <label className="hussar-form-label">{label}</label>
    {children}
  </div>
);

const ComplianceDemoOutput = ({ output }) => {
  const { t } = useTranslation();
  return (
    <Card className="hussar-tc-card hussar-compliance-output" data-testid="compliance-output">
      <div className="hussar-compliance-output-badge" data-testid="compliance-output-human-badge">
        <UserCheck className="w-4 h-4" /> {t('compliance.output.humanReviewBadge')}
      </div>

      <Section icon={FileText} label={t('compliance.output.summaryLabel')} testid="compliance-output-summary">
        <p>{output.summary}</p>
      </Section>
      <Section icon={ListChecks} label={t('compliance.output.missingLabel')} testid="compliance-output-missing">
        <ul className="hussar-pol-op-bullets">
          {output.missingInfo.map((m) => <li key={m}>{m}</li>)}
        </ul>
      </Section>
      <Section icon={ShieldCheck} label={t('compliance.output.checklistLabel')} testid="compliance-output-checklist">
        <ul className="hussar-pol-op-bullets">
          {output.humanReviewChecklist.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </Section>
      <Section icon={CalendarClock} label={t('compliance.output.deadlinesLabel')} testid="compliance-output-deadlines">
        {output.deadlines.length === 0
          ? <p className="hussar-tc-meta">—</p>
          : <ul className="hussar-pol-op-bullets">
              {output.deadlines.map((d) => <li key={d}>{d}</li>)}
            </ul>}
      </Section>
      <Section icon={Mail} label={t('compliance.output.draftLabel')} testid="compliance-output-draft">
        <pre className="hussar-compliance-output-draft">{output.draftMessage}</pre>
      </Section>
      <ReviewSignalsSection
        signals={output.reviewSignals}
        framing={output.reviewSignalsFraming}
        disclaimer={output.reviewSignalsDisclaimer}
        testidPrefix="compliance-output"
      />
      <Section icon={AlertTriangle} label={t('compliance.output.disclaimerLabel')} testid="compliance-output-disclaimer">
        <p className="hussar-pol-op-body">{output.disclaimer}</p>
      </Section>
    </Card>
  );
};

const Section = ({ icon: Icon, label, testid, children }) => (
  <div className="hussar-compliance-output-section" data-testid={testid}>
    <div className="hussar-compliance-output-section-label">
      <Icon className="w-4 h-4" /> {label}
    </div>
    <div className="hussar-compliance-output-section-body">{children}</div>
  </div>
);

export default ComplianceOperator;
